/**
 * One member's graded rounds, one row per round.
 *
 * The words "Skipped" and "Missed" are the server's `participation` value
 * with the casing results and history use (SUR-R87). This file never decides
 * which of the two a round was by looking at its picks. A round with picks
 * names each picked team and shows that pick's result in the words the Game
 * tab uses.
 *
 * Nothing here drops a pick. `/results/get-by-member` already builds its
 * picks from locked picks only, so an unlocked pick never arrives. A second
 * filter here would be a second copy of that rule.
 */
import type { ReactNode } from "react";

import {
  SURVIVOR_OUTCOME_LOST,
  SURVIVOR_OUTCOME_PENDING,
  SURVIVOR_OUTCOME_TIE,
  SURVIVOR_OUTCOME_WON,
} from "../survivorGame/survivorGameCopy";

export const SURVIVOR_ROUNDS_OPEN_LABEL = "View your rounds";
export const SURVIVOR_ROUNDS_CLOSE_LABEL = "Hide your rounds";
export const SURVIVOR_ROUNDS_LOADING_LABEL = "Loading your rounds";
export const SURVIVOR_ROUNDS_FAILED_MESSAGE = "We could not load your rounds.";
export const SURVIVOR_ROUNDS_RETRY_LABEL = "Try again";
export const SURVIVOR_ROUNDS_EMPTY_BODY =
  "Graded rounds appear here once a round has been graded.";
export const SURVIVOR_ROUNDS_SKIPPED_LABEL = "Skipped";
export const SURVIVOR_ROUNDS_MISSED_LABEL = "Missed";

export type typeSurvivorRoundPick = {
  eventId: string;
  teamId: string;
  outcome: "win" | "loss" | "push" | "pending";
};

export type typeSurvivorRound = {
  roundId: string;
  label: string;
  participation: "picked" | "skipped" | "missed";
  picks: readonly typeSurvivorRoundPick[];
};

export type typeSurvivorRoundEntry = {
  entryId: string;
  label: string;
  rounds: readonly typeSurvivorRound[];
};

export type typeSurvivorRoundTeamName = { name: string };

/**
 * Maps the server's participation token to the word a fan reads.
 * `picked` has no word of its own: that row shows its picks.
 * The argument is participation alone, so a picks array cannot change it.
 */
export function survivorRoundParticipationLabel(
  participation: typeSurvivorRound["participation"],
):
  | typeof SURVIVOR_ROUNDS_SKIPPED_LABEL
  | typeof SURVIVOR_ROUNDS_MISSED_LABEL
  | null {
  if (participation === "skipped") return SURVIVOR_ROUNDS_SKIPPED_LABEL;
  if (participation === "missed") return SURVIVOR_ROUNDS_MISSED_LABEL;
  return null;
}

/**
 * Maps a pick's outcome token to the word a fan reads. These are the Game
 * tab's own words for the same four outcomes, so one pick never reads two
 * ways on two tabs.
 */
export function survivorRoundPickResultLabel(
  outcome: typeSurvivorRoundPick["outcome"],
): string {
  switch (outcome) {
    case "win":
      return SURVIVOR_OUTCOME_WON;
    case "loss":
      return SURVIVOR_OUTCOME_LOST;
    case "push":
      return SURVIVOR_OUTCOME_TIE;
    case "pending":
      return SURVIVOR_OUTCOME_PENDING;
  }
}

/**
 * The picked team's name. A team the list does not know falls back to its
 * id, the same fallback the pick board uses, so a row is never blank.
 */
export function survivorRoundPickTeamName(
  teamId: string,
  teamsById: ReadonlyMap<string, typeSurvivorRoundTeamName>,
): string {
  const name = teamsById.get(teamId)?.name;
  return name === undefined || name === "" ? teamId : name;
}

const PARTICIPATION_CLASS =
  "w-fit rounded-full border border-hairline px-2 py-1 text-caption text-ink";

export const SurvivorRoundList = ({
  entries,
  teamsById,
}: {
  entries: readonly typeSurvivorRoundEntry[];
  teamsById: ReadonlyMap<string, typeSurvivorRoundTeamName>;
}): ReactNode => {
  const entriesWithRounds = entries.filter((entry) => entry.rounds.length > 0);

  if (entriesWithRounds.length === 0) {
    return (
      <p
        className="text-body text-ink-secondary"
        data-testid="survivor-rounds-empty"
      >
        {SURVIVOR_ROUNDS_EMPTY_BODY}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3" data-testid="survivor-rounds">
      {entriesWithRounds.map((entry) => (
        <section
          key={entry.entryId}
          className="flex flex-col gap-1"
          data-testid={`survivor-rounds-entry-${entry.entryId}`}
        >
          {entriesWithRounds.length > 1 ? (
            <h3 className="text-body text-ink">{entry.label}</h3>
          ) : null}
          <ul>
            {entry.rounds.map((round) => {
              const participationLabel = survivorRoundParticipationLabel(
                round.participation,
              );
              return (
                <li
                  key={round.roundId}
                  className="flex flex-col gap-1 border-t border-hairline px-4 py-2"
                  data-testid={`survivor-round-${entry.entryId}-${round.roundId}`}
                >
                  <span className="text-body text-ink">{round.label}</span>
                  {participationLabel === null ? null : (
                    <span
                      className={PARTICIPATION_CLASS}
                      data-testid={`survivor-round-participation-${entry.entryId}-${round.roundId}`}
                    >
                      {participationLabel}
                    </span>
                  )}
                  {round.picks.length === 0 ? null : (
                    <ul>
                      {round.picks.map((pick) => (
                        <li
                          key={`${round.roundId}-${pick.eventId}-${pick.teamId}`}
                          className="flex items-baseline justify-between gap-3 text-caption"
                          data-testid={`survivor-round-pick-${entry.entryId}-${round.roundId}-${pick.eventId}`}
                        >
                          <span
                            className="min-w-0 break-words text-ink"
                            data-testid={`survivor-round-pick-team-${entry.entryId}-${round.roundId}-${pick.eventId}`}
                          >
                            {survivorRoundPickTeamName(pick.teamId, teamsById)}
                          </span>
                          <span
                            className="shrink-0 text-ink-secondary"
                            data-testid={`survivor-round-pick-result-${entry.entryId}-${round.roundId}-${pick.eventId}`}
                          >
                            {survivorRoundPickResultLabel(pick.outcome)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
};
